import React from 'react';
import { Button, ConfirmationModal, toastError, toastSuccess } from 'bench-internal-theme';
import { useMutation } from '@apollo/client';
import { useClient } from '../../../ClientContext';
import { openModal } from 'modal-core-components';
import { Connection, ConnectionSource, NEW_INTEGRATION_SOURCES } from '../../types';
import { startCase } from 'lodash/string';
import { SYNC_CONNECTION, SYNC_NEW_INTEGRATION_CONNECTION } from '../../queries';
import refetchQueries from '../refetchQueries';
import moment from 'moment/moment';
import DateRangePickerModal from './DateRangePickerModal';

type SyncButtonProps = {
  connection: Connection;
};

const SyncButton = ({ connection }: SyncButtonProps) => {
  const { id: clientId } = useClient();

  const [sync, { loading }] = useMutation(SYNC_CONNECTION, {
    awaitRefetchQueries: true,
    onCompleted: () => {
      toastSuccess(
        'Connection',
        'Connection sync in progress. When it is complete, the ledger ' +
          'status will be updated and a monthly reconciliation file will be generated where applicable.',
      );
    },
    onError: () => {
      toastError('Connection', 'There was a problem syncing the connection.');
    },
    refetchQueries: refetchQueries(clientId),
  });

  const formattedSource = startCase(connection.source.toLowerCase());

  const isNewIntegration = NEW_INTEGRATION_SOURCES.includes(ConnectionSource[connection.source]);

  const [syncNewIntegration, { loading: syncNewIntegrationLoading }] = useMutation(
    SYNC_NEW_INTEGRATION_CONNECTION,
    {
      awaitRefetchQueries: true,
      onCompleted: () => {
        toastSuccess(
          'Connection',
          `${formattedSource} connection sync in progress. When it is complete, the ledger status will be updated and a monthly reconciliation file will be generated where applicable.`,
        );
      },
      onError: () => {
        toastError('Connection', `There was a problem syncing the ${formattedSource} connection.`);
      },
      refetchQueries: refetchQueries(clientId),
    },
  );

  const triggerSyncNewIntegration = (startDate: string, endDate: string) =>
    syncNewIntegration({
      variables: {
        clientId,
        sourceId: connection.sourceId,
        startDate,
        endDate,
        type: connection.source.toLowerCase(),
      },
    });

  const today = moment();

  const getMinValidDate = (source: keyof typeof ConnectionSource) => {
    if (source === 'SQUARE') return moment('2021-01-01');
    else if (source === 'PAYPAL') return moment().subtract(3, 'years');
    else if (source === 'AMAZON') return moment().subtract(2, 'months').startOf('month');
    else return undefined;
  };

  const getDefaultStartDate = () => {
    return moment().startOf('year');
  };

  const openDateRangePickerModal = () =>
    openModal(closeModal => (
      <DateRangePickerModal
        defaultStartDate={getDefaultStartDate()}
        defaultEndDate={today}
        minValidDate={getMinValidDate(connection.source)}
        closeModal={closeModal}
        description={`Choose the beginning and ending dates to sync transactions from`}
        onConfirm={(startDate: string, endDate: string) =>
          triggerSyncNewIntegration(startDate, endDate).then(closeModal)
        }
        title="Sync Range"
      />
    ));

  const triggerSync = () => {
    if (connection.source === ConnectionSource.AMAZON) {
      const startDate = moment().subtract(2, 'months').startOf('month');
      triggerSyncNewIntegration(startDate.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')).then();
    } else sync({ variables: { connectionId: connection.id } });
  };

  const openConfirmationModal = () =>
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        onConfirm={triggerSync}
        description="Hit confirm to start syncing. "
        title="Are you sure?"
      />
    ));

  const onClick = () => {
    if (isNewIntegration && connection.source !== ConnectionSource.AMAZON) {
      return openDateRangePickerModal();
    }

    return openConfirmationModal();
  };

  return (
    <Button
      icon="refresh"
      disabled={
        loading ||
        connection.state !== 'CONNECTED' ||
        syncNewIntegrationLoading ||
        connection.isSyncing
      }
      styleType="primary"
      onClick={onClick}
    >
      Sync
    </Button>
  );
};

export default SyncButton;
